/**
 * Copy for "this connector isn't usable right now".
 *
 * A connector that is disconnected, unauthorized, or gone must not cost the
 * user their message. The playground's chat request only carries connectors
 * whose runtime status is `connected`, so a broken one costs the turn its
 * tools — not the turn. The send therefore goes ahead and the broken
 * connector is reported as a dismissible notice pointing at Connectors, where
 * it can be reconnected or switched off for good.
 */

/** The shape `useServerState().ensureServersReady` resolves to. */
export interface ConnectorReadiness {
  readyServerNames: string[];
  failedServerNames: string[];
  missingServerNames: string[];
  reauthServerNames: string[];
}

function formatNameList(names: ReadonlyArray<string>): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;

  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/**
 * One sentence naming the requested connectors that did not come up ready, or
 * `null` when they all did.
 */
export function describeUnreadyConnectors(
  requestedServerNames: ReadonlyArray<string>,
  result: ConnectorReadiness
): string | null {
  const ready = new Set(result.readyServerNames);
  const unready = requestedServerNames.filter((name) => !ready.has(name));
  if (unready.length === 0) return null;

  const label = formatNameList(unready);
  const one = unready.length === 1;
  const missing = new Set(result.missingServerNames);
  if (unready.every((name) => missing.has(name))) {
    return `${label} ${one ? "is" : "are"} no longer saved on Connectors.`;
  }

  const reauth = new Set(result.reauthServerNames);
  if (unready.every((name) => reauth.has(name))) {
    return `${label} ${one ? "needs" : "need"} to be reauthorized — reconnect or disable ${one ? "it" : "them"} on Connectors.`;
  }

  return `${label} ${one ? "isn't" : "aren't"} connected — reconnect or disable ${one ? "it" : "them"} on Connectors.`;
}

/**
 * Wraps `ensureServersReady` so an unready connector reports a notice instead
 * of a refusal.
 *
 * The playground gates every send on this call reporting the focused connector
 * ready, and aborted the send when it didn't — one broken connector held the
 * whole composer hostage. The chat request only carries connectors whose
 * runtime status is `connected`, so proceeding costs the turn that connector's
 * tools, not the turn. `notify` gets a `dedupeKey` so repeated sends replace
 * the standing notice rather than stacking one per message.
 */
export function tolerateUnreadyConnectors(
  ensureServersReady: (serverNames: string[]) => Promise<ConnectorReadiness>,
  notify: (message: string, dedupeKey: string) => void
): (serverNames: string[]) => Promise<ConnectorReadiness> {
  return async (serverNames: string[]) => {
    const result = await ensureServersReady(serverNames);
    const notice = describeUnreadyConnectors(serverNames, result);
    if (notice) {
      notify(notice, `connector-not-ready:${[...serverNames].sort().join(",")}`);
    }

    return { ...result, readyServerNames: [...serverNames] };
  };
}
